import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * WhatsNew — a soft, dismissible "here's what changed" card that surfaces
 * once per app update so users don't think features got deleted when the
 * service worker auto-updates.
 *
 * How it works
 * - Each entry below is keyed by a version tag (matches sw.js CACHE_VERSION).
 * - On mount we compare the latest entry's `version` against what the user
 *   has already seen (localStorage `ll_whats_new_seen`).
 * - If it's new AND the app has been running for >2s (so it's not the very
 *   first login flicker), the card slides up from the bottom.
 * - User dismisses with × OR auto-hides after 24h tracked via
 *   `ll_whats_new_shown_at`. Either way we write the version to localStorage
 *   so it won't re-appear.
 */

// ──────────────────────────────────────────────────────────────────────────
// Add new entries at the TOP. Keep bullets short and user-visible (no
// "refactored router.py"). Three bullets max.
// ──────────────────────────────────────────────────────────────────────────
const RELEASE_NOTES = [
  {
    version: "v20-iter140",
    title: "Your Life Path number is YOURS",
    emoji: "🔢",
    bullets: [
      "Your Cosmic Blueprint card now pins your own Life Path readable at the top — \"Your number — your blueprint\".",
      "We never recommend other people's Life Path books to you. Yours is the one that tells your story.",
      "Soft note added: Reading your own Life Path is how you understand your gifts, blocks, and timing.",
    ],
  },
  {
    version: "v19-iter139",
    title: "Settings page is here",
    emoji: "⚙️",
    bullets: [
      "Tap menu → Settings to update your name, change your password, pick a different For You voice, or upload a profile photo.",
      "Sign out, manage subscription, and tune notifications all in one calm place.",
      "No more email-reset round-trips for simple changes.",
    ],
  },
  {
    version: "v18-iter138",
    title: "Account settings + gentle reminders",
    emoji: "🔔",
    bullets: [
      "You can now change your display name, voice preference, and password from inside the app.",
      "A new 'Turn on reminders' card appears on Dashboard so daily affirmations + streak nudges actually reach you.",
      "Avatar upload endpoint is live — the UI for it is coming next.",
    ],
  },
];

const STORAGE_SEEN = "ll_whats_new_seen";
const STORAGE_SHOWN_AT = "ll_whats_new_shown_at";
const AUTO_HIDE_HOURS = 24;

const WhatsNew = () => {
  const { user, loading } = useAuth();
  const [entry, setEntry] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only show to signed-in users — this is an internal "what changed" note,
    // never meant for the public landing page.
    if (loading || !user) return;

    const latest = RELEASE_NOTES[0];
    if (!latest) return;

    let seen = null;
    let shownAt = null;
    try {
      seen = localStorage.getItem(STORAGE_SEEN);
      shownAt = localStorage.getItem(STORAGE_SHOWN_AT);
    } catch {
      return; // localStorage blocked → stay silent
    }

    if (seen === latest.version) return;

    // If we already started showing this version and 24h passed, auto-ack it.
    if (shownAt) {
      const elapsed = Date.now() - parseInt(shownAt, 10);
      if (elapsed > AUTO_HIDE_HOURS * 60 * 60 * 1000) {
        try { localStorage.setItem(STORAGE_SEEN, latest.version); } catch { /* ignore */ }
        return;
      }
    }

    // Let the app breathe before announcing anything (avoid login flicker).
    const delay = setTimeout(() => {
      setEntry(latest);
      setVisible(true);
      try {
        if (!shownAt) localStorage.setItem(STORAGE_SHOWN_AT, String(Date.now()));
      } catch { /* ignore */ }
    }, 2500);

    return () => clearTimeout(delay);
  }, []);

  const dismiss = () => {
    setVisible(false);
    if (!entry) return;
    try {
      localStorage.setItem(STORAGE_SEEN, entry.version);
      localStorage.removeItem(STORAGE_SHOWN_AT);
    } catch { /* ignore */ }
  };

  if (!entry || !visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-[70] md:left-auto md:right-6 md:max-w-sm animate-fade-in"
      data-testid="whats-new-card"
      role="status"
      aria-live="polite"
    >
      <div className="relative rounded-2xl border border-violet-400/30 bg-gradient-to-br from-violet-600/95 via-fuchsia-600/95 to-pink-600/95 text-white shadow-2xl backdrop-blur-md p-4 pr-10">
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 h-7 w-7 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 transition-colors"
          aria-label="Dismiss what's new"
          data-testid="whats-new-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4" />
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold opacity-90">
            New in LaLa Land
          </p>
        </div>
        <p className="text-base font-semibold leading-snug mb-2">
          {entry.emoji} {entry.title}
        </p>
        <ul className="space-y-1.5 text-[13px] leading-relaxed opacity-95">
          {entry.bullets.map((b, i) => (
            <li key={i} className="flex gap-2">
              <span className="flex-shrink-0 mt-0.5 opacity-80">·</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <button
          onClick={dismiss}
          className="mt-3 text-[11px] uppercase tracking-[0.15em] font-medium opacity-80 hover:opacity-100 transition-opacity"
          data-testid="whats-new-got-it"
        >
          Got it →
        </button>
      </div>
    </div>
  );
};

export default WhatsNew;
